import { authenticatedFetch } from "@/api/session";
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8000";

function getAuthHeaders() {
  const token = localStorage.getItem("access_token");

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export type CareQueueStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "DISMISSED";

export type CareQueuePriority =
  | "LOW"
  | "MEDIUM"
  | "HIGH"
  | "CRITICAL";

export type CareActivity = {
  id: string;
  type: "COMPLETION_NOTE" | "REOPENED";
  comment: string | null;
  author_name: string | null;
  created_at: string;
};

export type CareQueueItem = {
  ministry_id?: string | null;
  member_name?: string | null;
  household_name?: string | null;
  activities?: CareActivity[];
  id: string;
  church_id: string;
  member_id?: string | null;
  household_id?: string | null;
  title: string;
  description?: string | null;
  priority: CareQueuePriority;
  status: CareQueueStatus;
  recommended_action: string;
  created_at: string;
  updated_at: string;
};

export type CareQueueListResponse = {
  items: CareQueueItem[];
  total: number;
};

export type CareQueueItemUpdate = {
  completion_comment?: string;
  title?: string;
  description?: string | null;
  priority?: CareQueuePriority;
  status?: CareQueueStatus;
  recommended_action?: string;
};

export type CareQueueGenerationResponse = {
  created: number;
  skipped: number;
  items: CareQueueItem[];
  skipped_items: {
    member_id: string;
    reason: string;
  }[];
};

async function parseResponse<T>(
  response: Response,
  errorMessage: string
): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);

    throw new Error(
      body && typeof body.detail === "string"
        ? body.detail
        : errorMessage
    );
  }

  return response.json() as Promise<T>;
}

export async function getCareQueue(
  churchId: string
): Promise<CareQueueListResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/care-queue?page_size=100`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load care queue");
}

export async function getMemberCareQueue(
  churchId: string,
  memberId: string
): Promise<CareQueueListResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/care-queue?member_id=${memberId}`,
    {
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(response, "Failed to load member care queue");
}

export async function updateCareQueueItem(
  churchId: string,
  itemId: string,
  payload: CareQueueItemUpdate
): Promise<CareQueueItem> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/care-queue/${itemId}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );

  return parseResponse(response, "Failed to update care item");
}

export async function generateAtRiskCareItems(
  churchId: string,
  days = 90
): Promise<CareQueueGenerationResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/care-queue/generate/at-risk?days=${days}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );

  return parseResponse(
    response,
    "Failed to refresh Care/Follow-up from intelligence"
  );
}